import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, Wallet, Users, CheckCircle2, TrendingUp, Scale, RefreshCw, CalendarClock, Trophy, Wrench } from "lucide-react";
import { formatBRL } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDAL = ["text-amber-400", "text-slate-300", "text-amber-700"];

export const DashboardTab = ({ onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [alertasManut, setAlertasManut] = useState([]);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const [d, c, m, v] = await Promise.all([
        axios.get(`${API}/dashboard`, { headers }),
        axios.get(`${API}/sellers/comissoes`, { headers }),
        axios.get(`${API}/maintenance/alertas`, { headers }),
        axios.get(`${API}/vehicles/admin`, { headers }),
      ]);
      setData(d.data);
      setRanking([...c.data].sort((a, b) => b.total_vendido_mes - a.total_vendido_mes || b.comissao_mes - a.comissao_mes));
      const vnames = Object.fromEntries(v.data.map((x) => [x.id, x.nome]));
      const lista = [];
      Object.entries(m.data || {}).forEach(([vid, itens]) => {
        itens.forEach((i) => lista.push({ ...i, veiculo: vnames[vid] || vid }));
      });
      lista.sort((a, b) => (a.status === b.status ? 0 : a.status === "vencido" ? -1 : 1));
      setAlertasManut(lista);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao carregar dashboard.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-28 rounded-2xl bg-[#131620] animate-pulse" />)}</div>;
  }

  const kpis = [
    { icon: Car, label: "Veículos em estoque", value: data.estoque_disponivel },
    { icon: Wallet, label: "Valor em estoque", value: formatBRL(data.valor_estoque) },
    data.margem_estimada != null && { icon: TrendingUp, label: "Margem prevista (estoque)", value: formatBRL(data.margem_estimada) },
    data.lucro_liquido_mes != null && { icon: TrendingUp, label: "Lucro líquido no mês", value: formatBRL(data.lucro_liquido_mes), tone: data.lucro_liquido_mes >= 0 ? "text-emerald-400" : "text-red-400" },
    { icon: CalendarClock, label: "Locação no mês", value: formatBRL(data.receita_locacao_mes || 0) },
    { icon: Users, label: "Leads no mês", value: data.leads_mes },
    { icon: CheckCircle2, label: "Vendas no mês", value: data.vendas_mes },
    { icon: CheckCircle2, label: "Ticket médio", value: formatBRL(data.ticket_medio) },
    data.saldo_mes != null && { icon: Scale, label: "Saldo do mês", value: formatBRL(data.saldo_mes), tone: data.saldo_mes >= 0 ? "text-emerald-400" : "text-red-400" },
  ].filter(Boolean);

  const maxDia = Math.max(1, ...data.leads_por_dia.map((d) => d.total));

  return (
    <div data-testid="dashboard-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Visão geral da loja</h1>
        <button onClick={load} data-testid="dashboard-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {alertasManut.length > 0 && (
        <div className="glass border-amber-500/40 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="dashboard-manutencao-alertas">
          <Wrench className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-semibold text-amber-300">Lembrete de manutenção: {alertasManut.length} pendência(s)</p>
            <ul className="mt-1 space-y-0.5 text-slate-300 text-xs">
              {alertasManut.map((a) => (
                <li key={a.id} data-testid={`dashboard-manutencao-${a.id}`}>
                  <span className={a.status === "vencido" ? "text-red-400 font-semibold" : "text-amber-400 font-semibold"}>
                    {a.status === "vencido" ? "Vencido" : "Próximo"}
                  </span>
                  {" — "}{a.veiculo}: {{ oleo: "Troca de óleo", pneus: "Pneus", revisao: "Revisão", freios: "Freios", outros: "Manutenção" }[a.tipo] || a.tipo}
                  {a.proximo_km ? ` (${a.proximo_km.toLocaleString("pt-BR")} km)` : ""}
                  {a.proxima_data ? ` (${a.proxima_data.split("-").reverse().join("/")})` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-kpis">
        {kpis.map((k) => (
          <div key={k.label} className="glass rounded-2xl p-5 flex items-center gap-4">
            <span className="w-11 h-11 rounded-2xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <k.icon className="w-5 h-5 text-emerald-400" />
            </span>
            <div className="min-w-0">
              <p className={`font-display text-xl sm:text-2xl font-bold truncate ${k.tone || "text-white"}`}>{k.value}</p>
              <p className="text-[11px] text-slate-400">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6" data-testid="dashboard-ranking">
          <h2 className="font-display font-bold text-white mb-5 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-emerald-400" /> Ranking de vendedores — {new Date().toLocaleDateString("pt-BR", { month: "long" })}
          </h2>
          {ranking.length === 0 ? (
            <p className="text-sm text-slate-500">Cadastre a equipe na aba Equipe para acompanhar o ranking.</p>
          ) : (
            <ul className="space-y-3">
              {ranking.map((r, i) => (
                <li key={r.vendedor.id} className="flex items-center gap-3 text-sm" data-testid={`ranking-row-${r.vendedor.id}`}>
                  <span className={`w-7 h-7 rounded-full glass flex items-center justify-center font-display font-bold text-xs shrink-0 ${MEDAL[i] || "text-slate-500"}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {r.vendedor.nome}
                      {!r.vendedor.ativo && <span className="text-[10px] text-slate-500 ml-2">(inativo)</span>}
                    </p>
                    <p className="text-[11px] text-slate-500">{r.vendas_mes} venda(s) · comissão {formatBRL(r.comissao_mes)}</p>
                  </div>
                  <span className={`font-semibold whitespace-nowrap ${r.total_vendido_mes > 0 ? "text-emerald-400" : "text-slate-500"}`} data-testid={`ranking-total-${r.vendedor.id}`}>
                    {formatBRL(r.total_vendido_mes)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="glass rounded-2xl p-6" data-testid="dashboard-chart">
          <h2 className="font-display font-bold text-white mb-5">Leads — últimos 14 dias</h2>
          <div className="flex items-end gap-1.5 h-32">
            {data.leads_por_dia.map((d) => (
              <div key={d.dia} className="flex-1 flex flex-col items-center gap-1.5">
                <span className="text-[10px] text-slate-500">{d.total > 0 ? d.total : ""}</span>
                <div
                  className="w-full rounded-t-md bg-emerald-500/70"
                  style={{ height: `${Math.max(4, (d.total / maxDia) * 100)}%` }}
                  title={`${d.dia}: ${d.total} lead(s)`}
                />
                <span className="text-[10px] text-slate-600">{d.dia}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="dashboard-recent">
          <h2 className="font-display font-bold text-white mb-4">Últimos leads</h2>
          {data.ultimos_leads.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum lead ainda.</p>
          ) : (
            <ul className="space-y-3">
              {data.ultimos_leads.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{l.nome}</p>
                    <p className="text-xs text-slate-500 truncate">{l.interesse}</p>
                  </div>
                  <span className="text-[11px] text-slate-500 whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleDateString("pt-BR") : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data.ultimas_vendas.length > 0 && (
            <>
              <h2 className="font-display font-bold text-white mt-7 mb-4">Últimas vendas</h2>
              <ul className="space-y-3">
                {data.ultimas_vendas.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                    <p className="text-white font-medium truncate">{s.cliente || "Cliente"}</p>
                    <span className="text-emerald-400 font-semibold whitespace-nowrap">{formatBRL(s.valor)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
